// A bounded, local attraction field inspired by graphics-atlas SoftSurface.press
// (compact spatial weighting) and its damped recovery. This is a tactile model,
// not an electromagnetic or ferrofluid simulation.
export class MagneticPutty {
  constructor(body,{surfacePoints=null}={}){
    this.body=body;
    this.surfacePoints=surfacePoints;
    this.extent=Math.max(...body.max.map((v,a)=>v-body.min[a]));
    const [x,y,z]=body.min,[X,Y,Z]=body.max,E=this.extent;
    this.home=[(x+X)*.5+.65*E,y+.65*(Y-y),Z+.1*E];
    this.position=[...this.home];this.enabled=false;this.anchor=null;
    this.displacement=[0,0,0];this.weights=new Float64Array(body.count);
    this.field={targets:new Float64Array(body.p.length),lambda:new Float64Array(body.p.length),compliance:.00012};
    this.field.targets.set(body.initialRest);
  }
  move(target){
    if(target?.length===3&&target.every(Number.isFinite))for(let a=0;a<3;a++)
      this.position[a]=Math.max(this.body.min[a]-.55*this.extent,Math.min(this.body.max[a]+.55*this.extent,target[a]));
  }
  setActive(active){this.enabled=Boolean(active);}
  get active(){return this.enabled||this.body.magneticField===this.field||this.position.some((v,a)=>Math.abs(v-this.home[a])>this.extent*1e-5);}
  reset(){
    this.enabled=false;this.anchor=null;this.displacement.fill(0);
    for(let a=0;a<3;a++)this.position[a]=this.home[a];
    this.field.targets.set(this.body.initialRest);this.field.lambda.fill(0);
    if(this.body.magneticField===this.field)this.body.magneticField=null;
  }
  step(dt){
    if(!Number.isFinite(dt)||dt<=0)return;
    dt=Math.min(dt,1/30);
    const body=this.body,E=this.extent;
    // A real hand always owns the object. No delayed attraction may fight it.
    if(body.hold){this.enabled=false;this.anchor=null;this.displacement.fill(0);body.magneticField=null;}
    if(!this.enabled){
      const k=1-Math.exp(-dt/.2);
      for(let a=0;a<3;a++){
        this.position[a]+=(this.home[a]-this.position[a])*k;
        if(Math.abs(this.position[a]-this.home[a])<E*1e-5)this.position[a]=this.home[a];
      }
    }
    if(this.enabled&&!this.anchor&&Math.hypot(...this.position.map((v,a)=>v-this.home[a]))>E*.08){
      let point=null;
      if(this.surfacePoints?.length){
        // Scan only during acquisition, never while solving or while a patch
        // is held. Folded skins must not attach to empty bounding-box corners.
        const p=this.surfacePoints;let closest=-1,best=Infinity;
        for(let i=0;i<p.length;i+=3){
          const d=(p[i]-this.position[0])**2+(p[i+1]-this.position[1])**2+(p[i+2]-this.position[2])**2;
          if(d<best){best=d;closest=i;}
        }
        if(closest>=0)point=[p[closest],p[closest+1],p[closest+2]];
      }else{
        point=this.position.map((v,a)=>Math.max(body.min[a],Math.min(body.max[a],v)));
        // If the tool crosses the bounds, retain a surface contact, not a point
        // in the interior. This also keeps acquisition independent of FPS.
        let axis=0,side=body.min[0],distance=Infinity;
        for(let a=0;a<3;a++)for(const face of [body.min[a],body.max[a]]){
          const d=Math.abs(point[a]-face);if(d<distance){distance=d;axis=a;side=face;}
        }
        point[axis]=side;
      }
      if(point&&Math.hypot(...point.map((v,a)=>v-this.position[a]))<E*.16){
        this.anchor=point;
        const radius=E*.48;
        for(let i=0;i<body.count;i++){
          const d=Math.hypot(body.initialRest[i*3]-point[0],body.initialRest[i*3+1]-point[1],body.initialRest[i*3+2]-point[2])/radius;
          this.weights[i]=d<1?(1-d*d)**2:0;
        }
      }
    }
    let x=0,y=0,z=0;
    if(this.enabled&&this.anchor){
      x=this.position[0]-this.anchor[0];y=this.position[1]-this.anchor[1];z=this.position[2]-this.anchor[2];
      const distance=Math.hypot(x,y,z),fade=Math.max(0,Math.min(1,(E*.8-distance)/(E*.3)));
      const scale=Math.min(1,E*.45/(distance||1))*fade;
      x*=scale;y*=scale;z*=scale;
      if(distance>E*.8)this.anchor=null;
    }
    // A broad neck catches up after the first visible response, then relaxes
    // more slowly than the rigid tool returns home.
    const goal=[x,y,z],k=1-Math.exp(-dt/(this.enabled?.13:.28));
    for(let a=0;a<3;a++)this.displacement[a]+=(goal[a]-this.displacement[a])*k;
    if(!this.anchor&&Math.hypot(...this.displacement)<E*.0005){
      this.displacement.fill(0);if(body.magneticField===this.field)body.magneticField=null;return;
    }
    if(!this.enabled&&Math.hypot(...this.displacement)<E*.0005){
      this.anchor=null;this.displacement.fill(0);if(body.magneticField===this.field)body.magneticField=null;return;
    }
    if(body.hold)return;
    for(let i=0;i<body.count;i++)for(let a=0;a<3;a++)this.field.targets[i*3+a]=body.initialRest[i*3+a]+this.displacement[a]*this.weights[i];
    body.magneticField=this.field;body.activity=Math.max(body.activity,.01);
  }
}
